import { useState, useEffect, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Search, Upload, FolderOpen, Image as ImageIcon, Check } from 'lucide-react'
import api from '@/lib/api/axios'
import { taskImageSrc } from '../lib/taskImage'
import { useCan } from '@/hooks/useCan'

interface TaskImagePickerProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    onSelect: (imagePath: string) => void
    currentImagePath?: string | null
}

interface ImageDirectory {
    name: string
    files: { name: string; path?: string }[]
}

const SCREENSHOTS = 'runbook-screenshots'

/**
 * The screenshot library, through the app's client: /api/images needs a signed-in user with
 * graphics.read (docs/rbac-permissions.md), and a raw fetch carried no token. The /images alias
 * is not routed by CloudFront in production, so it is /api/images here.
 *
 * The route answers with a flat { name: url } map of the folder; an older { directories } shape
 * is still accepted. Either becomes the directories this picker lists.
 */
async function fetchScreenshotLibrary(): Promise<ImageDirectory[]> {
    const { data } = await api.get('/api/images/library', { params: { type: SCREENSHOTS, path: SCREENSHOTS } })
    if (Array.isArray(data?.directories)) return data.directories
    const files = Object.values((data ?? {}) as Record<string, string>)
        .filter((url) => typeof url === 'string')
        .map((url) => ({ name: url.split('/').pop() ?? url, path: url }))
    return files.length > 0 ? [{ name: SCREENSHOTS, files }] : []
}

export function TaskImagePicker({ open, onOpenChange, onSelect, currentImagePath }: TaskImagePickerProps) {
    const { t } = useTranslation()
    const [directories, setDirectories] = useState<ImageDirectory[]>([])
    const [loading, setLoading] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')
    const [selectedImage, setSelectedImage] = useState<string | null>(currentImagePath || null)
    const [uploading, setUploading] = useState(false)
    const [uploadError, setUploadError] = useState<string | null>(null)
    const fileInputRef = useRef<HTMLInputElement>(null)
    // Choosing from the library needs graphics.read; adding to it, graphics.create.
    const canUpload = useCan('graphics.create')

    // Fetch image library
    useEffect(() => {
        if (!open) return
        setLoading(true)
        fetchScreenshotLibrary()
            .then(setDirectories)
            .catch(() => setDirectories([]))
            .finally(() => setLoading(false))
    }, [open])

    // Reset on close
    useEffect(() => {
        if (!open) {
            setSearchTerm('')
            setUploadError(null)
            setSelectedImage(currentImagePath || null)
        }
    }, [open, currentImagePath])

    // Filter images by search
    const filteredDirectories = directories.map(dir => ({
        ...dir,
        files: dir.files.filter(f =>
            !searchTerm || f.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            dir.name.toLowerCase().includes(searchTerm.toLowerCase())
        )
    })).filter(dir => dir.files.length > 0)

    const allImages = filteredDirectories.flatMap(dir =>
        dir.files.map(f => ({
            name: f.name,
            path: f.path || `${SCREENSHOTS}/${dir.name}/${f.name}`,
            directory: dir.name,
        }))
    )

    const handleSelect = () => {
        if (selectedImage) {
            onSelect(selectedImage)
            onOpenChange(false)
        }
    }

    const handleUpload = useCallback(async (file: File) => {
        if (!file) return
        const allowed = ['image/png', 'image/jpeg', 'image/gif', 'image/webp']
        if (!allowed.includes(file.type)) {
            setUploadError('Only PNG, JPEG, GIF, and WebP images are supported')
            return
        }
        if (file.size > 10 * 1024 * 1024) {
            setUploadError('Image must be under 10MB')
            return
        }

        setUploading(true)
        setUploadError(null)

        try {
            // 'file' is the field the route's multer reads; it was sent as 'image', which the route
            // refused as "No file provided".
            const formData = new FormData()
            formData.append('file', file)
            formData.append('directory', SCREENSHOTS)

            const { data: result } = await api.post('/api/images/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            })
            const imagePath = result?.url || result?.path || `${SCREENSHOTS}/${file.name}`

            // Select the uploaded image
            setSelectedImage(imagePath)

            // Refresh the library; the upload stands even if the refresh does not.
            await fetchScreenshotLibrary().then(setDirectories).catch(() => undefined)
        } catch (err) {
            setUploadError('Failed to upload image. Please try again.')
        } finally {
            setUploading(false)
        }
    }, [])

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[700px] max-h-[80vh]">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <ImageIcon className="h-5 w-5" />
                        {t('objects.runbook.selectImage', 'Select Task Image')}
                    </DialogTitle>
                    <DialogDescription>
                        {t('objects.runbook.selectImageDescription', 'Choose an image from the library or upload a new screenshot.')}
                    </DialogDescription>
                </DialogHeader>

                <Tabs defaultValue="library" className="w-full">
                    <TabsList>
                        <TabsTrigger value="library">
                            <FolderOpen className="h-4 w-4 mr-1.5" />
                            {t('objects.runbook.imageLibrary', 'Library')}
                        </TabsTrigger>
                        {canUpload && (
                            <TabsTrigger value="upload">
                                <Upload className="h-4 w-4 mr-1.5" />
                                {t('objects.runbook.uploadImage', 'Upload')}
                            </TabsTrigger>
                        )}
                    </TabsList>

                    <TabsContent value="library" className="space-y-3">
                        {/* Search */}
                        <div className="relative">
                            <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <Input
                                placeholder={t('objects.runbook.searchImages', 'Search images...')}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-9"
                            />
                        </div>

                        {/* Image grid */}
                        <div className="max-h-[380px] overflow-y-auto rounded-md border p-2">
                            {loading ? (
                                <div className="flex items-center justify-center h-32 text-muted-foreground text-sm">
                                    {t('common.loading', 'Loading...')}
                                </div>
                            ) : allImages.length === 0 ? (
                                <div className="flex flex-col items-center justify-center h-32 text-muted-foreground text-sm gap-2">
                                    <ImageIcon className="h-8 w-8 opacity-50" />
                                    {t('objects.runbook.noImages', 'No images found. Upload a screenshot to get started.')}
                                </div>
                            ) : (
                                <div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
                                    {allImages.map((img) => (
                                        <button
                                            key={img.path}
                                            className={`relative group rounded-md border-2 overflow-hidden aspect-square transition-all ${
                                                selectedImage === img.path
                                                    ? 'border-primary ring-2 ring-primary/30'
                                                    : 'border-transparent hover:border-muted-foreground/30'
                                            }`}
                                            onClick={() => setSelectedImage(img.path)}
                                            title={img.name}
                                        >
                                            <img
                                                src={taskImageSrc(img.path)}
                                                alt={img.name}
                                                className="w-full h-full object-cover"
                                                loading="lazy"
                                            />
                                            {selectedImage === img.path && (
                                                <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
                                                    <Check className="h-6 w-6 text-primary-foreground bg-primary rounded-full p-1" />
                                                </div>
                                            )}
                                            <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[10px] px-1 py-0.5 truncate opacity-0 group-hover:opacity-100 transition-opacity">
                                                {img.name}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </TabsContent>

                    {canUpload && (
                        <TabsContent value="upload" className="space-y-3">
                            <div
                                className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-primary/50 transition-colors"
                                onClick={() => fileInputRef.current?.click()}
                                onDragOver={(e) => { e.preventDefault(); e.stopPropagation() }}
                                onDrop={(e) => {
                                    e.preventDefault()
                                    e.stopPropagation()
                                    const file = e.dataTransfer.files[0]
                                    if (file) handleUpload(file)
                                }}
                            >
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/png,image/jpeg,image/gif,image/webp"
                                    className="hidden"
                                    onChange={(e) => {
                                        const file = e.target.files?.[0]
                                        if (file) handleUpload(file)
                                        e.target.value = ''
                                    }}
                                />
                                <Upload className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
                                <p className="text-sm font-medium">
                                    {uploading
                                        ? t('common.uploading', 'Uploading...')
                                        : t('objects.runbook.dropOrClick', 'Click or drag an image here to upload')
                                    }
                                </p>
                                <p className="text-xs text-muted-foreground mt-1">
                                    PNG, JPEG, GIF, WebP — Max 10MB
                                </p>
                            </div>

                            {uploadError && (
                                <p className="text-sm text-destructive">{uploadError}</p>
                            )}

                            {selectedImage && (
                                <div className="flex items-center gap-3 p-3 rounded-md border bg-muted/50">
                                    <img
                                        src={taskImageSrc(selectedImage)}
                                        alt=""
                                        className="h-16 w-16 rounded object-cover"
                                    />
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium truncate">{selectedImage.split('/').pop()}</p>
                                        <p className="text-xs text-muted-foreground">Selected image</p>
                                    </div>
                                </div>
                            )}
                        </TabsContent>
                    )}
                </Tabs>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>
                        {t('common.cancel')}
                    </Button>
                    <Button onClick={handleSelect} disabled={!selectedImage}>
                        {t('objects.runbook.useImage', 'Use This Image')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
